import type { FiatCurrency } from '@/shared/api/types'

/**
 * Показ денег. Ни одна функция здесь не считает — только оформляет
 * то, что пришло с сервера строкой.
 */

/**
 * Фиатная сумма как в макете.
 *
 * Две валюты — два разных правила, и это не придирка:
 *
 *   USD: `$24 860.45` — знак ПЕРЕД числом, разделитель дробной части
 *        точка, центы показываются ВСЕГДА, даже нулевые. `$5` вместо
 *        `$5.00` в столбце цен ломает выравнивание по разрядам
 *        и читается как «цена приблизительная».
 *
 *   RUB: `15 000 ₽` — знак ПОСЛЕ числа, разделитель запятая, нулевые
 *        копейки отбрасываются. `15 000,00 ₽` выглядит бухгалтерией,
 *        а не ценой.
 *
 * Разряды разделяются неразрывным пробелом: обычный дал бы браузеру
 * перенести строку внутри числа.
 *
 * Ничего не считается — только оформляется строка, пришедшая с сервера.
 */
export function fiat(amount: string | null | undefined, currency: FiatCurrency): string {
  // Бекенд отдаёт фиатный эквивалент необязательным: курса на момент
  // операции могло не быть. Прочерк честнее нуля — ноль читается как
  // «операция на ноль долларов».
  if (amount === null || amount === undefined || amount === '') return '—'
  const raw = amount.trim()
  const negative = raw.startsWith('-')
  const [whole = '0', frac = ''] = raw.replace('-', '').split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0')
  const minus = negative ? '\u2212' : ''

  if (currency === 'usd') {
    // Обрезаем, а не округляем: округление вверх на последнем центе —
    // это чужие деньги, показанные больше, чем они есть.
    const cents = (frac + '00').slice(0, 2)
    return `${minus}$${grouped}.${cents}`
  }

  /*
    Копейки: либо две цифры, либо ни одной.

    Обрезание хвостовых нулей давало «294 346,1 ₽» — одну цифру после
    запятой. Это не сумма, а загадка: десять копеек или одна? В рублях
    дробная часть всегда двузначная, и половина её ломает чтение
    ровно там, где сверяют деньги. Ноль копеек не показываем вовсе —
    так в макете.
  */
  const kopecks = (frac + '00').slice(0, 2)
  return `${minus}${grouped}${kopecks === '00' ? '' : `,${kopecks}`}\u00A0\u20BD`
}

/** Знак валюты отдельно — для осей графика и коротких подписей. */
export function fiatSign(currency: FiatCurrency): string {
  return currency === 'usd' ? '$' : '₽'
}

/**
 * Криптосумма для показа: хвостовые нули убираются, значащие знаки —
 * никогда. `0.00242915` читается, `0.002429150000` нет, а `0.0024` —
 * это уже другая сумма.
 */
export function crypto(amount: string | null | undefined): string {
  if (amount === null || amount === undefined || amount === '') return '—'
  if (!amount.includes('.')) return amount
  const trimmed = amount.replace(/0+$/, '').replace(/\.$/, '')
  return trimmed || '0'
}

/**
 * «+15%» или «−4.3%». Ноль — без знака.
 *
 * Округление через `toFixed` по МОДУЛЮ, а не через
 * `Math.round(x * 10) / 10`. Причина: `Math.round` округляет половину
 * в сторону плюс бесконечности, поэтому −42.5 даёт −42, и −4.25%
 * превращается в «−4.2%», хотя +4.25% даёт «+4.3%». Одна и та же
 * величина округляется по-разному в зависимости от знака.
 */
export function percent(value: string | number): string {
  const number = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(number) || number === 0) return '0%'
  const sign = number > 0 ? '+' : '−'
  const rounded = Math.abs(number).toFixed(1).replace(/\.0$/, '')
  return `${sign}${rounded}%`
}

/** «2 заявки в обработке» — с правильным окончанием. */
export function plural(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return `${count} ${one}`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} ${few}`
  return `${count} ${many}`
}

/** Короткий адрес: «bc1q…4hf8». Полный копируется кнопкой. */
export function shortAddress(address: string, head = 4, tail = 4): string {
  if (address.length <= head + tail + 1) return address
  return `${address.slice(0, head)}…${address.slice(-tail)}`
}
